// 파일 전송(설계서 §9.4 · v2 §3.11) — 세션 화면의 **붙여넣기·끌어다 놓기·Ctrl+C·진행·알림** 로직.
//
// 단축키 분기는 클립보드 훅(`useClipboardShare`)이 한 곳에서 한다 — 이 훅은 "파일이면 이렇게" 만 제공한다:
//  - 붙여넣기·끌어다 놓기: DataTransfer 에 파일이 있으면 보낸다(원격 = 스테이징 → 파일 목록 클립보드 → 붙여넣기).
//  - Ctrl+C: 원격 파일 가져오기. 브라우저는 폴더 선택 창을 **사용자 클릭 안에서만** 열 수 있어서,
//    제안이 오면 곧바로 거절하고 알림 [저장] → 폴더 선택 → `pull(copy:false)` 로 **그때 다시** 받는다
//    (원격이 사람의 클릭을 기다리며 전송을 붙잡지 않게).
// 🔴 알림에는 파일 이름을 넣지 않는다(개수·크기만).
//
// 통합앱(IS_SHELL)은 입력·출력 경로가 다르다(files/native.ts):
//  - Ctrl+V: 네이티브가 OS 파일 클립보드(탐색기에서 복사한 파일)를 읽는다 — 브라우저는 붙여넣기에 파일을 싣지 않는다.
//  - 끌어다 놓기: Tauri 창 drag-drop(경로 → 네이티브 핸들). 웹뷰 HTML5 놓기는 앱에서 오지 않는다.
//  - Ctrl+C: 알림 [내 PC로 받기] → 폴더를 고를 필요 없이 앱 스테이징에 받고 **내 파일 클립보드**에 올린다 → 탐색기에서 Ctrl+V(+[폴더 열기]).
//    (2026-10-20 부터 곧바로 받지 않는다 — 원격 안의 복사·붙여넣기가 내 PC 를 거쳐 한 바퀴 돌았다. copyRemote 주석.)
//    macOS 앱은 파일 URL 붙여넣기 판 → Finder 에서 Cmd+V(문구는 `LOCAL_FILE_MANAGER`·`LOCAL_PASTE_KEYS`).
import { useCallback, useEffect, useRef, useState } from 'react';
import { IS_MAC_VIEWER } from '../clipboard/useClipboardShare';
import { IS_SHELL } from '../shell';
import type { Session } from '../sessions/types';
import { FEATURE, featureAllowed } from '../features';
import { loadFlag, saveFlag } from '../prefs';
import type { AbortFlag, Progress } from './channel';
import {
  NativeStagingSink,
  readNativeFileClipboard,
  releaseNativeHandle,
  sourceFromDrop,
  type NativeDrop,
  type NativeFileSource,
} from './native';
import { FILE_ERR, FileError, describeEntries, type Dest, type Entry, type Offer } from './protocol';
import { DirectorySink, canPickDirectory, pickDirectory, type FileSink } from './sink';
import { BrowserFileSource, hasFiles, type FileSource } from './source';

/** 알림 문구의 내 PC 파일 관리자·붙여넣기 단축키 — macOS 뷰어는 Finder·Cmd+V(통합앱 Mac 도 파일 URL 붙여넣기 판으로 같다). */
const LOCAL_FILE_MANAGER = IS_MAC_VIEWER ? 'Finder' : '탐색기';
const LOCAL_PASTE_KEYS = IS_MAC_VIEWER ? 'Cmd+V' : 'Ctrl+V';

/** 알림 표시 시간. 버튼이 달린 알림은 누를 시간을 넉넉히 준다. */
const NOTICE_MS = { ok: 4000, err: 8000, action: 30000 } as const;
/** 진행 표시 갱신 간격(초당 수백 번 오는 조각마다 다시 그리지 않는다). */
const PROGRESS_THROTTLE_MS = 200;
/** 끌어내기 켬/끔을 기억하는 키(이 앱에만 — 사람마다 습관이 다르다). */
const DRAGOUT_PREF_KEY = 'rs.files.dragout';

export interface TransferView {
  direction: 'up' | 'down';
  done: number;
  total: number;
  /** 알림용 요약(개수·크기). */
  summary: string;
}

export interface FileNotice {
  kind: 'ok' | 'err';
  text: string;
  action?: { label: string; run: () => void };
}

export interface FileTransfer {
  /** 이 세션에서 파일을 보낼 수 있는가(에이전트 file 채널 + 제어 허용). */
  available: boolean;
  /** 원격 파일을 가져올 수 있는가(Ctrl+C). */
  canPull: boolean;
  transfer: TransferView | null;
  notice: FileNotice | null;
  dismissNotice(): void;
  /** DataTransfer 에 파일이 있으면 보내고 true, 없으면 false(호출측이 클립보드 경로로). **이벤트 처리 중에** 불러야 한다. */
  sendDataTransfer(dt: DataTransfer | null, dest: Dest): boolean;
  /** Ctrl+C — 원격 파일 가져오기. 'not_files' 면 호출측이 클립보드 경로로 넘긴다(복사는 이미 됐다). */
  copyRemote(): Promise<'handled' | 'not_files' | 'unchanged'>;
  /**
   * 브라우저 Ctrl+V 에 붙여넣을 것이 없었다 — 탐색기에서 복사한 **파일은 브라우저가 붙여넣기로 주지 않는다**(Chromium 실측).
   * 파일을 보내려던 것일 수 있으니 대신할 방법을 알린다. 알렸으면 true.
   */
  showPasteHint(): boolean;
  /**
   * 통합앱 Ctrl+V — 내 클립보드에 **탐색기에서 복사한 파일**이 있으면 보내고 true, 없으면 false(호출측이 텍스트·이미지로).
   * 브라우저에서는 늘 false.
   */
  sendNativeClipboard(): Promise<boolean>;
  /**
   * 통합앱 끌어다 놓기 결과를 보낸다. `dest` 가 null 이면(원격 화면 밖에 놓음) 보내지 않고 네이티브 핸들만 놓는다.
   */
  sendNativeDrop(d: NativeDrop, dest: Dest | null): void;
  /** 진행 중인 전송 취소. */
  cancel(): void;
  /** 다른 전송이 도는 중 — 전송은 **한 번에 한 건**이라 파일 패널 버튼을 잠근다. */
  busy: boolean;
  /**
   * 파일 패널 — 내 PC 칸에서 고른 것을 원격의 **연 폴더**(`dir`)로 보낸다.
   * 붙여넣기 경로와 달리 원격 화면을 건드리지 않는다(클립보드·단축키 없음).
   */
  sendToDir(source: Promise<FileSource | null>, dir: string, onDone?: () => void): void;
  /** 파일 패널 — 원격이 준 제안을 내 PC 칸의 폴더에 받는다. */
  receiveInto(offer: Offer, sink: FileSink, label: string, onDone?: () => void): void;
  /** 끌어내기(F5) — 원격에서 끌던 것을 뷰어 창 밖으로 낼 수 있는가(통합앱 + 에이전트가 `dragout` 을 알릴 때만). */
  canDragOut: boolean;
  /** 끌어내기를 켜 두었는가([⋯] 메뉴 켬/끔 — 이 앱에 기억, 기본 켬). 끄면 모든 끌기가 원격 안의 끌기다. */
  dragOutOn: boolean;
  toggleDragOut(): void;
  /** 끌어내기 — 영상을 벗어난 지점(정규화 좌표)을 알린다. 끌던 것이 파일이 아니면 조용히 넘어간다. */
  dragOut(nx: number, ny: number): void;
}

function errorText(e: unknown): string {
  return e instanceof FileError ? e.message : `파일 전송에 실패했습니다: ${(e as Error)?.message ?? e}`;
}

export function useFileTransfer(session: Session): FileTransfer {
  const channel = session.client.files;
  // 기능 권한(§14.19.2) `file` 이 없으면 끈다(붙여넣기·끌어다 놓기·Ctrl+C 의 파일 경로 전부).
  const available =
    !!session.fileCaps && !!channel && session.controlAllowed !== false &&
    featureAllowed(session.features, FEATURE.FILE);
  const canPull = available && !!session.fileCaps?.features.includes('pull');
  // 끌어내기는 **통합앱 전용**이다 — 브라우저는 파일을 영상 밖으로 끌어낼 수단이 없다(설계서 §9.4).
  const canDragOut = IS_SHELL && available && !!session.fileCaps?.features.includes('dragout');
  const [dragOutOn, setDragOutOn] = useState(() => loadFlag(DRAGOUT_PREF_KEY, true));
  const toggleDragOut = useCallback(() => {
    setDragOutOn((on) => {
      saveFlag(DRAGOUT_PREF_KEY, !on);
      return !on;
    });
  }, []);

  const [transfer, setTransfer] = useState<TransferView | null>(null);
  const [notice, setNotice] = useState<FileNotice | null>(null);
  // 전송이 도는 중인가 — `transfer` 는 출처를 다 읽은 뒤에야 생기므로(큰 폴더는 수백 ms) 따로 둔다.
  const [busy, setBusy] = useState(false);
  const noticeTimer = useRef<number | undefined>(undefined);
  // 진행 중인 전송의 취소 수단(보내기 = 표시 · 받기 = 채널에 알림).
  const abortRef = useRef<{ flag: AbortFlag; cancelReceive?: () => void } | null>(null);
  const lastPaint = useRef(0);

  useEffect(() => () => window.clearTimeout(noticeTimer.current), []);

  const show = useCallback((n: FileNotice) => {
    window.clearTimeout(noticeTimer.current);
    setNotice(n);
    const ms = n.action ? NOTICE_MS.action : NOTICE_MS[n.kind];
    noticeTimer.current = window.setTimeout(() => setNotice(null), ms);
  }, []);
  const dismissNotice = useCallback(() => {
    window.clearTimeout(noticeTimer.current);
    setNotice(null);
  }, []);
  const fail = useCallback(
    (e: unknown) => {
      if (e instanceof FileError && e.code === FILE_ERR.CANCELED) {
        show({ kind: 'ok', text: '파일 전송을 취소했습니다.' });
        return;
      }
      show({ kind: 'err', text: errorText(e) });
    },
    [show],
  );

  /** 진행 표시를 시작하고, 갱신 함수를 돌려준다. */
  const begin = useCallback((direction: TransferView['direction'], entries: Entry[], total: number) => {
    const summary = describeEntries(entries);
    setTransfer({ direction, done: 0, total, summary });
    lastPaint.current = 0;
    return (p: Progress) => {
      const now = performance.now();
      if (now - lastPaint.current < PROGRESS_THROTTLE_MS && p.done < p.total) {
        return;
      }
      lastPaint.current = now;
      setTransfer({ direction, done: p.done, total: p.total, summary });
    };
  }, []);

  /** 보내기 공통(붙여넣기·끌어다 놓기·선택 창) — 한 번에 한 건, 진행 표시·결과 알림·취소. */
  const startSend = useCallback(
    (sourceP: Promise<FileSource | null>, dest: Dest, onDone?: () => void) => {
      if (!channel) {
        return;
      }
      if (abortRef.current) {
        show({ kind: 'err', text: new FileError(FILE_ERR.BUSY).message });
        // 보내지 못한 출처도 놓는다(통합앱 네이티브 핸들).
        void sourceP.then((s) => s?.dispose?.(), () => undefined);
        return;
      }
      const flag: AbortFlag = { aborted: false };
      abortRef.current = { flag };
      setBusy(true);
      void (async () => {
        let source: FileSource | null = null;
        try {
          source = await sourceP;
          if (!source) {
            throw new FileError(FILE_ERR.LOCAL_READ, '파일을 읽지 못했습니다');
          }
          const total = source.entries.reduce((s, e) => s + (e.dir ? 0 : e.size), 0);
          const progress = begin('up', source.entries, total);
          await channel.send(source, dest, progress, flag);
          // 바로 가기·링크는 따라가지 않는다(설계서 §9.4 보안) — 빠진 것이 있으면 알린다.
          const skipped = source.skipped ? ` (바로 가기·링크 ${source.skipped}개는 건너뜀)` : '';
          // 패널(dir)은 "연 폴더에 올림", 그 밖(paste·drop)은 원격 화면에 붙여넣기다.
          const where = dest.kind === 'dir' ? '원격 폴더에 올림' : '원격에 붙여넣기';
          show({ kind: 'ok', text: `${where} — ${describeEntries(source.entries)}${skipped}` });
        } catch (e) {
          fail(e);
        } finally {
          source?.dispose?.();
          abortRef.current = null;
          setBusy(false);
          setTransfer(null);
          onDone?.();
        }
      })();
    },
    [channel, begin, show, fail],
  );

  const sendDataTransfer = useCallback(
    (dt: DataTransfer | null, dest: Dest): boolean => {
      if (!available || !channel || !hasFiles(dt)) {
        return false;
      }
      // ⚠️ 항목 목록은 이 호출 **안에서 동기로** 꺼내진다(fromDataTransfer 의 첫 await 전) — 이벤트가 끝나면 DataTransfer 가 비워진다.
      startSend(BrowserFileSource.fromDataTransfer(dt), dest);
      return true;
    },
    [available, channel, startSend],
  );

  const showPasteHint = useCallback((): boolean => {
    if (!available) {
      return false;
    }
    show({
      kind: 'err',
      text: `붙여넣을 내용이 없습니다. ${LOCAL_FILE_MANAGER}에서 복사한 파일은 브라우저가 붙여넣기로 주지 않습니다 — 파일을 원격 화면에 끌어다 놓거나 [🗂 파일] 창에서 보내세요(통합앱에서는 ${LOCAL_PASTE_KEYS} 로 됩니다).`,
    });
    return true;
  }, [available, show]);

  /**
   * 원격의 **지금** 파일 클립보드를 제안으로 받는다(복사 단축키는 누르지 않는다) — [저장]·[내 PC로 받기] 공통.
   * 파일이 아니거나 비었으면 그 사유로 던진다.
   */
  const pullRemoteClipboard = useCallback(async (): Promise<Offer> => {
    if (!channel) {
      throw new FileError(FILE_ERR.UNSUPPORTED);
    }
    const r = await channel.pull(false);
    if (r.kind !== 'offer') {
      throw new FileError(r.kind === 'not_files' ? FILE_ERR.NOT_FILES : FILE_ERR.UNCHANGED);
    }
    return r.offer;
  }, [channel]);

  /** [저장] — 폴더를 고른 뒤 원격 파일 클립보드를 **다시** 요청해 받는다(클릭 안에서 불린다). */
  const saveRemoteFiles = useCallback(() => {
    if (!channel) {
      return;
    }
    // 폴더 선택 창은 사용자 동작 안에서만 열린다 → 다른 await 보다 **먼저** 부른다.
    const dirP = pickDirectory();
    void (async () => {
      const flag: AbortFlag = { aborted: false };
      try {
        const dir = await dirP;
        const offer = await pullRemoteClipboard();
        const sink = new DirectorySink(dir);
        abortRef.current = { flag, cancelReceive: () => channel.cancelReceive(offer.x) };
        const progress = begin('down', offer.entries, offer.total);
        await channel.receive(offer, sink, progress);
        show({ kind: 'ok', text: `내 PC(${sink.label})에 저장됨 — ${describeEntries(offer.entries)}` });
      } catch (e) {
        // 폴더 선택 창을 닫은 것(AbortError)은 조용히 넘어간다.
        if ((e as DOMException)?.name === 'AbortError') {
          return;
        }
        fail(e);
      } finally {
        abortRef.current = null;
        setTransfer(null);
      }
    })();
  }, [channel, pullRemoteClipboard, begin, show, fail]);

  /**
   * 통합앱: 원격에서 복사한 파일을 곧바로 앱 스테이징에 받고 내 파일 클립보드에 올린다(사람이 폴더를 고를 필요 없음).
   * Ctrl+C 가 오래 붙잡히지 않게 받기는 뒤에서 흐르고, 진행은 도구막대에·결과는 알림에 나온다.
   */
  const receiveIntoApp = useCallback(
    (offer: Offer, origin: 'copy' | 'dragout' = 'copy') => {
      if (!channel) {
        return;
      }
      const flag: AbortFlag = { aborted: false };
      const sink = new NativeStagingSink();
      abortRef.current = { flag, cancelReceive: () => channel.cancelReceive(offer.x) };
      void (async () => {
        try {
          const progress = begin('down', offer.entries, offer.total);
          await channel.receive(offer, sink, progress);
          const summary = describeEntries(offer.entries);
          // [폴더 열기] — 붙여넣을 곳이 탐색기가 아니거나 바로 보고 싶을 때(스테이징은 붙여넣으면 옮겨져 비워진다).
          const reveal = { label: '폴더 열기', run: () => sink.reveal() };
          // 끌어내기(F5 3단계) — 손이 아직 버튼을 누르고 있으면 **진짜 끌어다 놓기**로 넘긴다.
          // 🔴 받기가 끝난 뒤에만 부른다(끌기는 놓을 때까지 돌아오지 않는 모달이라, 받는 중에 부르면 받기가 멈춘다).
          if (origin === 'dragout' && (await sink.startDrag())) {
            show({ kind: 'ok', text: `끌어다 놓았습니다 — ${summary}`, action: reveal });
            return;
          }
          // 이미 손을 뗐다(대개 이쪽이다 — 전송이 사람의 손보다 느리다) → 클립보드·[폴더 열기]로 마무리한다.
          const lead = origin === 'dragout' ? '끌어낸 파일을 내 PC에 받았습니다' : '내 PC 클립보드에 넣었습니다';
          show(
            sink.clipboardSet
              ? {
                  kind: 'ok',
                  text: `${lead} — ${summary}. ${LOCAL_FILE_MANAGER}에서 ${LOCAL_PASTE_KEYS} 로 붙여넣으세요.`,
                  action: reveal,
                }
              : { kind: 'ok', text: `내 PC에 받았습니다 — ${summary}`, action: reveal },
          );
        } catch (e) {
          fail(e);
        } finally {
          abortRef.current = null;
          setTransfer(null);
        }
      })();
    },
    [channel, begin, show, fail],
  );

  /**
   * 통합앱 [내 PC로 받기] — 원격 파일 클립보드를 **다시** 요청해(복사 단축키 없이) 앱 스테이징에 받는다.
   * Ctrl+C 때 받은 제안은 이미 돌려보냈다(아래 copyRemote) — 그 사이 원격 클립보드가 바뀌었으면 지금 것을 받는다.
   */
  const receiveRemoteClipboardIntoApp = useCallback(() => {
    if (!channel) {
      return;
    }
    if (abortRef.current) {
      show({ kind: 'err', text: new FileError(FILE_ERR.BUSY).message });
      return;
    }
    void (async () => {
      try {
        receiveIntoApp(await pullRemoteClipboard());
      } catch (e) {
        fail(e);
      }
    })();
  }, [channel, pullRemoteClipboard, receiveIntoApp, show, fail]);

  const copyRemote = useCallback(async (): Promise<'handled' | 'not_files' | 'unchanged'> => {
    if (!canPull || !channel) {
      return 'not_files';
    }
    const r = await channel.pull(true);
    if (r.kind !== 'offer') {
      return r.kind;
    }
    // 원격이 파일을 복사했다. 🔴 **곧바로 받지 않는다**(2026-10-20 사용자 보고) — 원격 탐색기 안에서 복사·붙여넣기를
    // 하려던 것일 수 있다(그쪽이 더 흔하다). 종전 통합앱은 Ctrl+C 마다 파일을 통째로 내 PC 로 내려받아,
    // 큰 폴더를 원격 안에서 옮기려 해도 회선으로 한 바퀴 돌았다. ⇒ 제안은 돌려보내고, 내 PC 로 가져올 때만 버튼으로 다시 받는다.
    // (원격 안에서 붙여넣는 Ctrl+V 는 원격 클립보드 그대로 쓰인다 — useClipboardShare `remoteOwns`.)
    channel.decline(r.offer);
    const summary = describeEntries(r.offer.entries);
    if (IS_SHELL) {
      show({ kind: 'ok', text: `원격에서 복사됨 — ${summary}`, action: { label: '내 PC로 받기', run: receiveRemoteClipboardIntoApp } });
      return 'handled';
    }
    if (!canPickDirectory()) {
      show({ kind: 'err', text: `원격에서 ${summary} 복사됨 — ${new FileError(FILE_ERR.NO_SAVE_API).message}` });
      return 'handled';
    }
    show({ kind: 'ok', text: `원격에서 복사됨 — ${summary}`, action: { label: '내 PC에 저장', run: saveRemoteFiles } });
    return 'handled';
  }, [canPull, channel, show, saveRemoteFiles, receiveRemoteClipboardIntoApp]);

  /**
   * 끌어내기(F5) — 원격에서 끌던 것을 **영상 밖으로** 냈다. 에이전트가 원격 끌기를 Esc 로 취소하고
   * 끌던 선택 항목을 제안하면 앱 스테이징에 받는다.
   *
   * 🔴 끌던 것이 파일이 아니면(`not_files`) **조용히** 넘어간다 — 사람은 글자나 그림을 끌었을 수도 있고,
   *    그때 오류 알림을 띄우면 원격 화면을 쓰는 내내 성가시다.
   */
  const dragOut = useCallback(
    (nx: number, ny: number) => {
      // 다른 전송이 도는 중이면 시작하지 않는다(전송은 한 번에 한 건 — 원격 끌기도 건드리지 않는다).
      if (!canDragOut || !dragOutOn || !channel || abortRef.current) {
        return;
      }
      void (async () => {
        try {
          const r = await channel.dragout(nx, ny);
          if (r.kind === 'offer') {
            receiveIntoApp(r.offer, 'dragout');
          }
        } catch (e) {
          fail(e);
        }
      })();
    },
    [canDragOut, dragOutOn, channel, receiveIntoApp, fail],
  );

  const sendNativeClipboard = useCallback(async (): Promise<boolean> => {
    if (!IS_SHELL || !available || !channel) {
      return false;
    }
    let source: NativeFileSource | null;
    try {
      source = await readNativeFileClipboard();
    } catch (e) {
      // 파일 목록은 있었는데 보낼 수 없다(너무 큼·예약 이름·클립보드 사용 중 등) — 텍스트로 넘기지 않고 이유를 알린다.
      fail(e);
      return true;
    }
    if (!source) {
      return false;
    }
    startSend(Promise.resolve(source), { kind: 'paste' });
    return true;
  }, [available, channel, startSend, fail]);

  const sendNativeDrop = useCallback(
    (d: NativeDrop, dest: Dest | null) => {
      if (!dest) {
        // 원격 화면 밖(탭 줄 등)에 놓았다 — 보내지 않는다.
        if (d.handle !== undefined) {
          releaseNativeHandle(d.handle);
        }
        return;
      }
      try {
        if (!available || !channel) {
          if (d.handle !== undefined) {
            releaseNativeHandle(d.handle);
          }
          throw new FileError(session.controlAllowed === false ? FILE_ERR.CONTROL_OFF : FILE_ERR.UNSUPPORTED);
        }
        startSend(Promise.resolve(sourceFromDrop(d)), dest);
      } catch (e) {
        fail(e);
      }
    },
    [available, channel, session.controlAllowed, startSend, fail],
  );

  /** 파일 패널 — 원격의 연 폴더로 올린다(진행·취소·알림은 다른 전송과 같은 한 자리를 쓴다). */
  const sendToDir = useCallback(
    (source: Promise<FileSource | null>, dir: string, onDone?: () => void) => {
      startSend(source, { kind: 'dir', dir }, onDone);
    },
    [startSend],
  );

  /** 파일 패널 — 원격 제안을 내 PC 칸의 폴더에 받는다. */
  const receiveInto = useCallback(
    (offer: Offer, sink: FileSink, label: string, onDone?: () => void) => {
      if (!channel) {
        return;
      }
      if (abortRef.current) {
        // 받지 않을 제안은 **돌려보낸다** — 원격이 응답을 기다리며 전송을 붙잡고 있지 않게.
        channel.decline(offer);
        show({ kind: 'err', text: new FileError(FILE_ERR.BUSY).message });
        return;
      }
      const flag: AbortFlag = { aborted: false };
      abortRef.current = { flag, cancelReceive: () => channel.cancelReceive(offer.x) };
      setBusy(true);
      void (async () => {
        try {
          const progress = begin('down', offer.entries, offer.total);
          await channel.receive(offer, sink, progress);
          show({ kind: 'ok', text: `내 PC(${label})에 저장됨 — ${describeEntries(offer.entries)}` });
        } catch (e) {
          fail(e);
        } finally {
          abortRef.current = null;
          setBusy(false);
          setTransfer(null);
          onDone?.();
        }
      })();
    },
    [channel, begin, show, fail],
  );

  const cancel = useCallback(() => {
    const a = abortRef.current;
    if (!a) {
      return;
    }
    a.flag.aborted = true;
    a.cancelReceive?.();
  }, []);

  return {
    available,
    canPull,
    transfer,
    notice,
    dismissNotice,
    sendDataTransfer,
    copyRemote,
    showPasteHint,
    sendNativeClipboard,
    sendNativeDrop,
    cancel,
    busy,
    sendToDir,
    receiveInto,
    canDragOut,
    dragOutOn,
    toggleDragOut,
    dragOut,
  };
}
